import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { PERMISSIONS, hasRepaymentContext, type PermissionCode } from '@solar/contracts';

import { Button } from '../components/Button';
import { navIconForPath } from '../components/DashboardIcons';
import { useAuth } from '../features/auth/auth-context';
import { buildCustomerNavItems } from '../features/auth/customer-nav';
import {
  shouldShowCustomerNav,
  shouldShowOperationsNav,
  shouldShowStaffDashboard,
} from '../features/auth/portal-access';
import { customerDashboardApi } from '../features/customer-dashboard/customer-dashboard.api';
import { useSessionQuery as useQuery } from '../hooks/useSessionQuery';
import cleanEnergySidebarUrl from '../images/dashboard-clean-energy-sidebar.png';
import logoUrl from '../images/logo.png';
import { cx } from '../lib/class-names';
import { paths } from '../routes/paths';

import styles from './AppLayout.module.css';

interface NavItem {
  to: string;
  label: string;
  permission?: PermissionCode;
  end?: boolean;
}

interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
  visible?: (
    user: ReturnType<typeof useAuth>['user'],
    can: (permission: PermissionCode) => boolean,
  ) => boolean;
}

const STAFF_NAV_GROUPS: NavGroup[] = [
  {
    id: 'workspace',
    label: 'Workspace',
    items: [{ to: paths.dashboard, label: 'Dashboard', end: true }],
  },
  {
    id: 'operations',
    label: 'Operations',
    visible: (user, can) => shouldShowOperationsNav(user, can),
    items: [
      {
        to: paths.adminApplications,
        label: 'Applications',
        permission: PERMISSIONS.SUBSCRIPTIONS_READ,
      },
      {
        to: paths.adminDiscountsReview,
        label: 'Discounts review',
        permission: PERMISSIONS.SUBSCRIPTIONS_UPDATE,
      },
      { to: paths.adminCustomers, label: 'Customers', permission: PERMISSIONS.CUSTOMERS_READ },
      {
        to: paths.adminContracts,
        label: 'Contracts',
        permission: PERMISSIONS.SUBSCRIPTIONS_READ,
      },
      { to: paths.adminMandates, label: 'Mandates', permission: PERMISSIONS.MANDATES_READ },
      {
        to: paths.adminMandateCancellations,
        label: 'Mandate cancellations',
        permission: PERMISSIONS.MANDATES_READ,
      },
      { to: paths.adminBilling, label: 'Repayments', permission: PERMISSIONS.BILLING_READ },
      {
        to: paths.adminPaymentAttempts,
        label: 'Payment attempts',
        permission: PERMISSIONS.PAYMENT_ATTEMPTS_READ,
      },
      {
        to: paths.adminPayments,
        label: 'Payments',
        permission: PERMISSIONS.PAYMENTS_READ,
      },
      { to: paths.adminReports, label: 'Reports', permission: PERMISSIONS.REPORTS_READ },
    ],
  },
  {
    id: 'catalogue',
    label: 'Catalogue',
    items: [
      { to: paths.adminProducts, label: 'Products', permission: PERMISSIONS.PLANS_MANAGE },
      {
        to: paths.adminRepaymentPlans,
        label: 'Repayment plans',
        permission: PERMISSIONS.PLANS_MANAGE,
      },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    items: [
      { to: paths.adminUsers, label: 'Users', permission: PERMISSIONS.USERS_READ },
      { to: paths.adminRoles, label: 'Roles', permission: PERMISSIONS.ROLES_MANAGE },
      { to: paths.adminAuditLogs, label: 'Audit log', permission: PERMISSIONS.AUDIT_READ },
      {
        to: paths.adminNotificationOutbox,
        label: 'Notifications',
        permission: PERMISSIONS.NOTIFICATIONS_READ,
      },
      {
        to: paths.adminProviderBanks,
        label: 'Provider banks',
        permission: PERMISSIONS.PROVIDER_BANKS_READ,
      },
      {
        to: paths.adminSystemHealth,
        label: 'System health',
        permission: PERMISSIONS.SYSTEM_HEALTH_READ,
      },
    ],
  },
];

function buildCustomerNav(
  can: (permission: PermissionCode) => boolean,
  dashboard: Awaited<ReturnType<typeof customerDashboardApi.getDashboard>> | undefined,
): NavGroup[] {
  const items = buildCustomerNavItems(can, {
    hasRepaymentContext:
      dashboard === undefined
        ? false
        : hasRepaymentContext({
            hasCustomerProfile: dashboard.hasCustomerProfile,
            onboardingComplete: dashboard.onboardingComplete,
            applicationStatus: dashboard.applicationStatus,
          }),
    onboardingComplete: dashboard?.onboardingComplete ?? true,
  });

  return [{ id: 'customer', label: 'Menu', items }];
}

function buildStaffNav(
  user: ReturnType<typeof useAuth>['user'],
  can: (permission: PermissionCode) => boolean,
): NavGroup[] {
  return STAFF_NAV_GROUPS.filter((group) => group.visible === undefined || group.visible(user, can))
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.permission === undefined || can(item.permission)),
    }))
    .filter((group) => group.items.length > 0);
}

export function AppLayout(): React.JSX.Element {
  const { user, signOut, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const navigationRef = useRef<HTMLElement>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!navOpen) return;
    menuTriggerRef.current =
      document.activeElement instanceof HTMLButtonElement ? document.activeElement : null;
    navigationRef.current?.querySelector<HTMLAnchorElement>('a')?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setNavOpen(false);
        menuTriggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [navOpen]);

  const isCustomerPortal = shouldShowCustomerNav(user, can) && !shouldShowStaffDashboard(user, can);
  const isStaffOverview = !isCustomerPortal && location.pathname === paths.dashboard;

  const dashboardQuery = useQuery({
    queryKey: ['customer', 'dashboard'],
    queryFn: () => customerDashboardApi.getDashboard(),
    enabled: isCustomerPortal && can(PERMISSIONS.CUSTOMERS_READ),
    staleTime: 60_000,
  });

  const navGroups = isCustomerPortal
    ? buildCustomerNav(can, dashboardQuery.data)
    : buildStaffNav(user, can);
  const primaryRole = user?.roles[0]?.name ?? 'Staff account';

  const handleSignOut = async (): Promise<void> => {
    await signOut();
    void navigate(paths.login, { replace: true });
  };

  const closeNav = (): void => {
    setNavOpen(false);
    if (navOpen) menuTriggerRef.current?.focus();
  };

  return (
    <div className={cx(styles.shell, isCustomerPortal ? styles.customerShell : styles.staffShell)}>
      <a className={styles.skipLink} href="#main-content">
        Skip to main content
      </a>

      {navOpen && (
        <button
          type="button"
          className={styles.backdrop}
          aria-label="Close navigation menu"
          onClick={closeNav}
        />
      )}

      <aside
        ref={navigationRef}
        className={cx(styles.sidebar, navOpen && styles.sidebarOpen)}
        aria-label="Primary navigation"
      >
        <NavLink className={styles.brand} to={paths.dashboard} onClick={closeNav}>
          <img className={styles.brandLogo} src={logoUrl} alt="" />
          <span className={styles.brandText}>
            {isCustomerPortal ? (
              <>
                <span className={styles.brandWordmark}>SEAMONTECH</span>
                <span className={styles.brandTagline}>Solar Repayment Portal</span>
              </>
            ) : (
              <>
                <span className={styles.brandWordmark}>SEAMONTECH</span>
                <span className={styles.brandTagline}>Operations portal</span>
              </>
            )}
          </span>
        </NavLink>

        <nav className={styles.nav} id="app-primary-nav" aria-label="Primary">
          {navGroups.map((group) => (
            <section className={styles.navGroup} key={group.id} aria-labelledby={`nav-${group.id}`}>
              {!isCustomerPortal && (
                <h2 className={styles.navGroupLabel} id={`nav-${group.id}`}>
                  {group.label}
                </h2>
              )}
              <ul className={styles.navList}>
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end === true}
                      className={({ isActive }) =>
                        cx(styles.navLink, isActive && styles.navLinkActive)
                      }
                      onClick={closeNav}
                    >
                      <span className={styles.navIcon}>{navIconForPath(item.to)}</span>
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </nav>

        {isCustomerPortal && (
          <aside className={styles.cleanEnergyCard} aria-label="Clean energy message">
            <img
              className={styles.cleanEnergyImage}
              src={cleanEnergySidebarUrl}
              alt=""
              decoding="async"
              loading="lazy"
            />
            <p className={styles.cleanEnergyTitle}>Clean Energy</p>
            <p className={styles.cleanEnergyBody}>
              Brighter Tomorrow — powering Nigerian homes with solar.
            </p>
          </aside>
        )}

        {!isCustomerPortal && (
          <aside className={styles.staffEnergyCard} aria-label="Seamontech clean energy message">
            <img src={cleanEnergySidebarUrl} alt="" decoding="async" />
            <div>
              <strong>Cleaner energy for brighter Nigeria</strong>
              <span>Powering progress, one home at a time.</span>
            </div>
          </aside>
        )}

        <div className={styles.sidebarFooter}>
          {!isCustomerPortal && (
            <div className={styles.staffAccount}>
              <span className={styles.staffAvatar} aria-hidden="true">
                {user?.email.slice(0, 1).toUpperCase()}
              </span>
              <span className={styles.staffAccountText}>
                <strong>{primaryRole}</strong>
                <span className={styles.email}>{user?.email}</span>
              </span>
            </div>
          )}
          {isCustomerPortal && <span className={styles.email}>{user?.email}</span>}
          <div className={styles.accountLinks}>
            <NavLink to={paths.profile} onClick={closeNav}>
              Account
            </NavLink>
            <NavLink to={paths.sessions} onClick={closeNav}>
              Sessions
            </NavLink>
            <Button variant="secondary" onClick={() => void handleSignOut()}>
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      <div className={styles.contentShell}>
        <header
          className={cx(
            styles.topBar,
            !isCustomerPortal && styles.staffTopBar,
            isStaffOverview && styles.overviewTopBar,
          )}
        >
          <Button
            type="button"
            variant="secondary"
            className={styles.menuToggle}
            aria-expanded={navOpen}
            aria-controls="app-primary-nav"
            onClick={() => {
              setNavOpen((open) => !open);
            }}
          >
            {navOpen ? 'Close menu' : 'Menu'}
          </Button>
          <span className={styles.topBarContext}>
            <span>{isCustomerPortal ? 'Customer portal' : 'Staff workspace'}</span>
            <strong>{user?.email}</strong>
          </span>
        </header>

        <main className={styles.main} id="main-content" tabIndex={-1}>
          {/* Private forms, mutation state and blob previews must not survive an identity change. */}
          <Outlet key={user?.id ?? 'anonymous'} />
        </main>
      </div>
    </div>
  );
}
